import React from 'react';
import styled from 'styled-components';
import {Accordion, Detail, RichTextEditor, SegmentedControl, TextField, Toggle} from '@forecast-it/design-system';
import {useIntl, IntlShape} from 'react-intl';
import BrandColorSwatchPicker from './BrandColorSwatchPicker';
import {FONT_OPTIONS} from './config/fonts';
import {formatQuoteDate} from './quoteDocument';
import type {MergeField} from './config/mergeFields';
import type {PlateValue, QuoteDocumentData, QuoteRollup, QuoteTemplate} from './resolver/types';

type Props = {
	template: QuoteTemplate;
	onChange: (next: QuoteTemplate) => void;
	mergeFields: MergeField[];
	data: QuoteDocumentData;
};

const FieldRow = styled.div`
	margin-bottom: 12px;
`;
const Label = styled.div`
	font-size: 13px;
	margin-bottom: 4px;
`;
const GroupLabel = styled.div`
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: #727483;
	margin: 16px 0 8px 0;

	&:first-child {
		margin-top: 0;
	}
`;
const ToggleRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 10px;
	font-size: 13px;
`;
const NoteRow = styled.div`
	margin: 6px 0 10px 0;
`;
const SysFieldRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 7px 0;
	border-bottom: 1px solid #e7e7f0;
	font-size: 13px;

	&:last-child {
		border-bottom: 0;
	}
`;
const SysFieldValue = styled.span`
	font-weight: 600;
`;
// Plain styled <select>. The design system's ButtonDropdown is a trigger primitive
// (must be wrapped in DropdownList) and doesn't accept value/options/onChange — this
// keeps the stopgap's font picker simple.
const Select = styled.select`
	width: 100%;
	padding: 8px;
	font-size: 13px;
	border: 1px solid #d3d3df;
	border-radius: 4px;
	background: #fff;
`;

type SetField = <K extends keyof QuoteTemplate>(key: K, value: QuoteTemplate[K]) => void;

const LabelledToggle = ({
	label,
	checked,
	disabled,
	cy,
	onChange,
}: {
	label: string;
	checked: boolean;
	disabled?: boolean;
	cy?: string;
	onChange: (checked: boolean) => void;
}) => (
	<ToggleRow>
		<span>{label}</span>
		<Toggle checked={checked} onChange={onChange} disabled={disabled} cy={cy} />
	</ToggleRow>
);

const Note = ({children}: {children: React.ReactNode}) => (
	<NoteRow>
		<Detail color="auxiliary">{children}</Detail>
	</NoteRow>
);

const NarrativeEditor = ({
	value,
	disabled,
	placeholder,
	mergeFields,
	onChange,
}: {
	value: PlateValue;
	disabled: boolean;
	placeholder: string;
	mergeFields: MergeField[];
	onChange: (value: PlateValue) => void;
}) => (
	<RichTextEditor
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the DS editor types its value as its own Plate AST; ours is the persisted structural twin
		initialValue={value as any}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same DS Plate AST bridge as initialValue
		onChange={(next: any) => onChange(next as PlateValue)}
		labelMentions={mergeFields.map(field => ({id: field.id, text: field.label}))}
		minHeight="120px"
		maxHeight="260px"
		variant="default"
		placeholder={placeholder}
		disabled={disabled}
	/>
);

const onOffBadge = (intl: IntlShape, on: boolean): string =>
	intl.formatMessage({id: on ? 'quote_designer.badge.on' : 'quote_designer.badge.off'});

/**
 * The designer controls, grouped to mirror the document: Branding · Cover & details ·
 * Introduction · Scope of work · Estimate · Terms & conditions · Agreement · Export.
 */
const SettingsPanel: React.FC<Props> = ({template, onChange, mergeFields, data}) => {
	const intl = useIntl();
	const setField: SetField = (key, value) => onChange({...template, [key]: value});
	const rolesRollup = template.rollup === 'roles';
	// An expense-only baseline has no role rows, so the role-totals toggle would change nothing
	// visible — disabled rather than looking dead.
	const hasRoleItems = data.lineItems.some(item => item.kind === 'role');

	return (
		<Accordion selectionType={Accordion.SelectionType.MULTIPLE} background={false} defaultValue="estimate">
			<Accordion.Item
				value="branding"
				label={intl.formatMessage({id: 'quote_designer.section.branding'})}
				content=""
				data-cy="quote-section-branding"
			>
				<FieldRow>
					<Label>{intl.formatMessage({id: 'quote_designer.field.brand_color'})}</Label>
					<BrandColorSwatchPicker value={template.brandColor} onChange={color => setField('brandColor', color)} />
				</FieldRow>
				<FieldRow>
					<Label>{intl.formatMessage({id: 'quote_designer.field.font'})}</Label>
					<Select
						aria-label={intl.formatMessage({id: 'quote_designer.field.font'})}
						value={template.font}
						onChange={event => setField('font', event.target.value as QuoteTemplate['font'])}
					>
						{FONT_OPTIONS.map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
				</FieldRow>
				<Note>{intl.formatMessage({id: 'quote_designer.branding.logo_note'})}</Note>
			</Accordion.Item>

			<Accordion.Item
				value="cover"
				label={intl.formatMessage({id: 'quote_designer.section.cover'})}
				content=""
				data-cy="quote-section-cover"
			>
				<Note>{intl.formatMessage({id: 'quote_designer.cover.description'})}</Note>
				<SysFieldRow>
					<span>{intl.formatMessage({id: 'quote_designer.cover.quote_date'})}</span>
					<SysFieldValue>{formatQuoteDate(data.issuedDate, intl)}</SysFieldValue>
				</SysFieldRow>
				<SysFieldRow>
					<span>
						{intl.formatMessage({id: 'quote_designer.cover.expiry_date'})}{' '}
						<Detail color="auxiliary">{intl.formatMessage({id: 'quote_designer.cover.expiry_hint'})}</Detail>
					</span>
					<SysFieldValue>{formatQuoteDate(data.expiryDate, intl)}</SysFieldValue>
				</SysFieldRow>
				<SysFieldRow>
					<span>{intl.formatMessage({id: 'quote_designer.cover.reference'})}</span>
					<SysFieldValue>{data.projectIndicator}</SysFieldValue>
				</SysFieldRow>
				<SysFieldRow>
					<span>{intl.formatMessage({id: 'quote_designer.cover.version'})}</span>
					<SysFieldValue>{`v${data.revision}`}</SysFieldValue>
				</SysFieldRow>
			</Accordion.Item>

			<Accordion.Item
				value="introduction"
				label={intl.formatMessage({id: 'quote_designer.section.introduction'})}
				content={onOffBadge(intl, template.showIntroduction)}
				contentAlignment={Accordion.Item.Alignment.RIGHT}
				data-cy="quote-section-introduction"
			>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.intro.include'})}
					checked={template.showIntroduction}
					onChange={checked => setField('showIntroduction', checked)}
					cy="quote-toggle-introduction"
				/>
				<NarrativeEditor
					value={template.introduction}
					disabled={!template.showIntroduction}
					placeholder={intl.formatMessage({id: 'quote_designer.intro.placeholder'})}
					mergeFields={mergeFields}
					onChange={value => setField('introduction', value)}
				/>
				<Note>{intl.formatMessage({id: 'quote_designer.merge_tokens_note'})}</Note>
			</Accordion.Item>

			<Accordion.Item
				value="scope"
				label={intl.formatMessage({id: 'quote_designer.section.scope'})}
				content={onOffBadge(intl, template.showScopeOfWork)}
				contentAlignment={Accordion.Item.Alignment.RIGHT}
				data-cy="quote-section-scope"
			>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.scope.include'})}
					checked={template.showScopeOfWork}
					onChange={checked => setField('showScopeOfWork', checked)}
					cy="quote-toggle-scope"
				/>
				<NarrativeEditor
					value={template.scopeOfWork}
					disabled={!template.showScopeOfWork}
					placeholder={intl.formatMessage({id: 'quote_designer.scope.placeholder'})}
					mergeFields={mergeFields}
					onChange={value => setField('scopeOfWork', value)}
				/>
				<Note>{intl.formatMessage({id: 'quote_designer.narrative_note'})}</Note>
			</Accordion.Item>

			<Accordion.Item
				value="estimate"
				label={intl.formatMessage({id: 'quote_designer.section.estimate'})}
				content=""
				data-cy="quote-section-estimate"
			>
				<GroupLabel>{intl.formatMessage({id: 'quote_designer.estimate.detail_level'})}</GroupLabel>
				<SegmentedControl
					value={template.rollup}
					onValueChange={(value: QuoteRollup) => setField('rollup', value)}
					data-cy="quote-rollup"
				>
					{[
						{value: 'roles', labelId: 'quote_designer.estimate.rollup.roles'},
						{value: 'phase', labelId: 'quote_designer.estimate.rollup.phase'},
						...(data.useDeliverables
							? [{value: 'deliverable', labelId: 'quote_designer.estimate.rollup.deliverable'}]
							: []),
						{value: 'project', labelId: 'quote_designer.estimate.rollup.project'},
					].map(item => (
						<SegmentedControl.Item
							key={item.value}
							value={item.value}
							label={intl.formatMessage({id: item.labelId})}
							data-cy={`quote-rollup-${item.value}`}
						>
							{intl.formatMessage({id: item.labelId})}
						</SegmentedControl.Item>
					))}
				</SegmentedControl>

				<GroupLabel>{intl.formatMessage({id: 'quote_designer.estimate.columns'})}</GroupLabel>
				<LabelledToggle
					label={intl.formatMessage({
						id:
							data.estimationUnit === 'days'
								? 'quote_designer.estimate.show_time_days'
								: 'quote_designer.estimate.show_time_hours',
					})}
					checked={template.showTime}
					onChange={checked => setField('showTime', checked)}
					cy="quote-toggle-time"
				/>
				<Note>{intl.formatMessage({id: 'quote_designer.estimate.time_note'})}</Note>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.estimate.show_rate'})}
					checked={template.showRate}
					onChange={checked => setField('showRate', checked)}
					cy="quote-toggle-rate"
				/>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.estimate.show_role_totals'})}
					checked={template.showRoleTotals}
					disabled={!rolesRollup || !hasRoleItems}
					onChange={checked => setField('showRoleTotals', checked)}
					cy="quote-toggle-role-totals"
				/>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.estimate.show_phase_subtotals'})}
					checked={template.showPhaseSubtotals}
					disabled={!rolesRollup}
					onChange={checked => setField('showPhaseSubtotals', checked)}
					cy="quote-toggle-phase-subtotals"
				/>
				<LabelledToggle
					label={intl.formatMessage({
						id: rolesRollup
							? 'quote_designer.estimate.show_phase_expenses'
							: 'quote_designer.estimate.fold_expenses',
					})}
					checked={template.showPhaseExpenses}
					onChange={checked => setField('showPhaseExpenses', checked)}
					cy="quote-toggle-phase-expenses"
				/>
				{data.useDeliverables && (
					<LabelledToggle
						label={intl.formatMessage({id: 'quote_designer.estimate.show_deliverables'})}
						checked={template.showDeliverables}
						onChange={checked => setField('showDeliverables', checked)}
						cy="quote-toggle-deliverables"
					/>
				)}

				<GroupLabel>{intl.formatMessage({id: 'quote_designer.estimate.totals_rows'})}</GroupLabel>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.estimate.show_subtotal'})}
					checked={template.showSubtotal}
					onChange={checked => setField('showSubtotal', checked)}
					cy="quote-toggle-subtotal"
				/>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.estimate.show_expenses'})}
					checked={template.showExpensesRow}
					onChange={checked => setField('showExpensesRow', checked)}
					cy="quote-toggle-expenses-row"
				/>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.estimate.show_grand_total'})}
					checked={template.showGrandTotal}
					onChange={checked => setField('showGrandTotal', checked)}
					cy="quote-toggle-grand-total"
				/>
				<Note>{intl.formatMessage({id: 'quote_designer.estimate.note'})}</Note>
			</Accordion.Item>

			<Accordion.Item
				value="terms"
				label={intl.formatMessage({id: 'quote_designer.section.terms'})}
				content={onOffBadge(intl, template.showTerms)}
				contentAlignment={Accordion.Item.Alignment.RIGHT}
				data-cy="quote-section-terms"
			>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.terms.include'})}
					checked={template.showTerms}
					onChange={checked => setField('showTerms', checked)}
					cy="quote-toggle-terms"
				/>
				<NarrativeEditor
					value={template.terms}
					disabled={!template.showTerms}
					placeholder={intl.formatMessage({id: 'quote_designer.terms.placeholder'})}
					mergeFields={mergeFields}
					onChange={value => setField('terms', value)}
				/>
				<Note>{intl.formatMessage({id: 'quote_designer.terms.note'})}</Note>
			</Accordion.Item>

			<Accordion.Item
				value="agreement"
				label={intl.formatMessage({id: 'quote_designer.section.agreement'})}
				content={intl.formatMessage({id: 'quote_designer.agreement.always_included'})}
				contentAlignment={Accordion.Item.Alignment.RIGHT}
				data-cy="quote-section-agreement"
			>
				<Note>{intl.formatMessage({id: 'quote_designer.agreement.description'})}</Note>
			</Accordion.Item>

			<Accordion.Item
				value="export"
				label={intl.formatMessage({id: 'quote_designer.section.export'})}
				content=""
				data-cy="quote-section-export"
			>
				<FieldRow>
					<TextField
						label={intl.formatMessage({id: 'quote_designer.export.file_name'})}
						value={template.fileNameTemplate}
						onValueChange={(value: string) => setField('fileNameTemplate', value)}
						helperText={intl.formatMessage(
							{id: 'quote_designer.export.file_name_hint'},
							{reference: '{reference}', client: '{client}', project: '{project}', date: '{date}'}
						)}
						data-cy="quote-file-name"
					/>
				</FieldRow>
			</Accordion.Item>
		</Accordion>
	);
};

export default SettingsPanel;
